import React, { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Breadcrumbs from '@decisiv/ui-components/lib/components/Breadcrumbs';
import {
  H1,
  H3,
  H4,
  P,
} from '@decisiv/ui-components/lib/components/Typography';
import {
  Button,
  Grid,
  Tag,
  Toggle,
  useNotifications,
} from '@decisiv/ui-components';
import iconix from '@decisiv/iconix';
import { t, Trans } from '@lingui/macro';
import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import noop from 'lodash/noop';

import IntegrationsApi from '../../api/integrations';
import ConfiguredIntegrationsApi from '../../api/configured_integrations';
import Form from '../../components/LocationSettingsForm';
import WrapperWithLoading from '../../components/WrapperWithLoading';
import NotFound from '../../components/NotFound';
import {
  EnableIntegration,
  HalfMoonBg,
  HeaderBlock,
  LocationContainer,
  PartnerRow,
  RemoveIntegration,
  SharedSettingsSection,
  SharedSettingGroup,
  SyncOptOut,
} from './style';
import { locationStateTag } from './syncStatus';
import SharedSettingsApi from '../../api/shared_settings';
import RemoveLocationModal from './RemoveLocationModal';
import CascadeSummary from './CascadeSummary';
// Same info affordance the Global Settings sections use, so the help pattern is
// identical across pages. Worth promoting to src/components/ if a third page needs it.
import FeatureInfoDrawer from '../IntegrationDetailPage/FeatureInfoDrawer';
import SyncOptOutInfoContent from './SyncOptOutInfoContent';
import LocationExecutionLog from '../../components/LocationExecutionLog';
import ChangeHistoryContainer from '../../containers/ChangeHistoryContainer';
import authorizeOn from '../../utils/resourceAuthorizer';
import Helmet from '../../components/SEO/Helmet';
import CopyUrlButton from '../../components/CopyUrlButton';
import { isUUID } from '../../utils/identifierUtils';
import {
  parseAttributeKey,
  getHeaderValue,
} from '../../features/dynamic-form/core/attributeKey';

export default function LocationSettingsPage() {
  const navigate = useNavigate();
  const [integration, setIntegration] = useState({});
  const [locationSettingsSchema, setLocationSettingsSchema] = useState({});
  const [location, setLocation] = useState({});
  const [toggle, setToggle] = useState('disabled');
  const [toggleDisabled, setToggleDisabled] = useState(false);
  const [toggleBg, setToggleBg] = useState(color.base.fullMoon);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [formKey, setFormKey] = useState(1);
  const [locationNotFound, setLocationNotFound] = useState(false);
  const [linkedSourceData, setLinkedSourceData] = useState(null);
  const [allSharedSettings, setAllSharedSettings] = useState([]);
  // An empty list means "no group" only once this has resolved.
  const [sharedSettingsLoading, setSharedSettingsLoading] = useState(true);
  // Bumped after a cascade so the partner tags cannot contradict the summary
  // reporting what just changed. Cheaper than refetching the whole page.
  const [peerStatesVersion, setPeerStatesVersion] = useState(0);

  const { identifier } = useParams();
  // Named to avoid shadowing `location`, which here is the configured integration.
  const routerLocation = useLocation();
  // The whole sync payload, not just the results: the banner's copy depends on
  // which lifecycle action cascaded.
  const [cascade, setCascade] = useState(
    routerLocation.state?.sharedSettingSync || null,
  );
  const [syncActions, setSyncActions] = useState('included');
  // Where this location stands in each linked integration, keyed by integration
  // uuid. Survives a reload, unlike the cascade summary.
  const [peerStates, setPeerStates] = useState(null);

  const handleModal = useCallback(() => setVisible(!visible), [visible]);

  useEffect(() => {
    if (!authorizeOn()) {
      navigate('/integrations');
      return undefined;
    }

    // The component is reused across :identifier changes, so a request for the
    // location just navigated away from can still land after the new one.
    let cancelled = false;

    async function getData() {
      // The component survives an :identifier change, so both of these have to
      // be reset per fetch: otherwise a bad URL keeps rendering Not Found over
      // the next location, and the previous location stays on screen — and
      // editable — until the replacement arrives.
      setLocationNotFound(false);
      setLoading(true);

      const configuredIntegration = isUUID(identifier)
        ? await ConfiguredIntegrationsApi.fetchConfiguredIntegrationsByUuid(
            identifier,
          )
        : await ConfiguredIntegrationsApi.fetchConfiguredIntegrationsBySlug(
            identifier,
          );

      if (cancelled) {
        return;
      }

      if (
        configuredIntegration.errors?.[0]?.status === 404 ||
        !configuredIntegration.data
      ) {
        setLocationNotFound(true);
        setLoading(false);
        return;
      }

      const data = configuredIntegration.data[0] || configuredIntegration.data;

      if (!data || _.isEmpty(data)) {
        setLocationNotFound(true);
        setLoading(false);
        return;
      }

      if (data) {
        const fetchSingleResponse =
          await IntegrationsApi.fetchIntegrationByUuid(
            data.attributes?.integration_uuid,
          );

        if (cancelled) {
          return;
        }

        setIntegration(fetchSingleResponse.data);

        const schema =
          fetchSingleResponse.data.attributes.location_settings_schema;

        const commandsFlow = fetchSingleResponse.data.attributes.commands_flow;

        // This is a workaround because of how lodash handles sorts on object it removes the named key so we insert it
        // and them we sort and group it
        let prevAttrIndex = '1';
        schema.forEach((attribute, i) => {
          let attrIndex = attribute.name.match(/\d+/);
          if (attrIndex === null) {
            attrIndex = prevAttrIndex;
          }

          const commandIndex = (parseInt(attrIndex, 10) - 1) / 2;
          const commandName = commandsFlow[commandIndex];
          schema[i].sort_index = commandIndex;
          schema[i].command_name = `${commandName}${attrIndex}`;

          prevAttrIndex = attrIndex;
        });

        const sortedSchema = _.sortBy(schema, ['sort_index']);

        setLocationSettingsSchema(_.groupBy(sortedSchema, 'command_name'));

        const existingAttrsMap = Object.fromEntries(
          (data.attributes.location_settings_attributes || []).map((a) => [
            a.name,
            a.value,
          ]),
        );
        const syncedAttrs = sortedSchema.map((attr) => ({
          name: attr.name,
          value: existingAttrsMap[attr.name] ?? '',
        }));

        setLocation({
          ...data,
          attributes: {
            ...data.attributes,
            location_settings_attributes: syncedAttrs,
          },
        });
        setToggle(data.attributes?.enabled ? 'enabled' : 'disabled');
        setSyncActions(
          data.attributes?.ignore_shared_setting_actions
            ? 'excluded'
            : 'included',
        );
        setToggleBg(
          data.attributes?.enabled
            ? color.status.success.light
            : color.base.fullMoon,
        );
      } else {
        setLocation({});
        setToggle('disabled');
      }
      setLoading(false);
    }

    getData();
    setVisible(false);
    setRefresh(false);

    return () => {
      cancelled = true;
    };
  }, [identifier, location.length, refresh]);

  useEffect(() => {
    async function fetchSettings() {
      const { data } = await SharedSettingsApi.fetchAllSharedSettings();
      if (data) {
        setAllSharedSettings(
          data.map((item) => ({ id: item.id, ...item.attributes })),
        );
      }
      setSharedSettingsLoading(false);
    }
    fetchSettings();
  }, []);

  const current = `${location.attributes?.name} (ID ${location.attributes?.srm_account_id})`;
  const customPath = integration.attributes?.slug
    ? integration.attributes.slug
    : integration.id;
  const path = [
    { to: '/integrations', text: 'Integrations' },
    {
      to: `/integrations/${customPath}`,
      text: integration.attributes?.name,
    },
  ].filter((crumb) => crumb.text);

  const sidebarSharedSettings = integration.id
    ? allSharedSettings.filter((s) =>
        (s.integration_uuids || []).includes(integration.id),
      )
    : [];

  // Deliberately the unfiltered lookup: a peer that is disabled or opted out of
  // sync is exactly what the panel has to surface, and the enabled-only lookup
  // used for Load Values would hide it.
  useEffect(() => {
    const srmAccountId = location.attributes?.srm_account_id;
    if (!integration?.id || !srmAccountId) return;
    if (
      !allSharedSettings.some((s) =>
        (s.integration_uuids || []).includes(integration.id),
      )
    ) {
      return;
    }

    async function loadPeerStates() {
      const { data } =
        await ConfiguredIntegrationsApi.fetchAllConfiguredIntegrationsBySrmAccountId(
          srmAccountId,
        );
      if (!data) return;

      // The id rides along so the panel can link straight to this location in
      // the partner integration.
      setPeerStates(
        Object.fromEntries(
          data.map((ci) => [
            ci.attributes.integration_uuid,
            { id: ci.id, ...ci.attributes },
          ]),
        ),
      );
    }

    loadPeerStates();
  }, [
    integration?.id,
    location.attributes?.srm_account_id,
    allSharedSettings,
    refresh,
    peerStatesVersion,
  ]);

  useEffect(() => {
    const srmAccountId = location.attributes?.srm_account_id;
    if (!integration?.id || !srmAccountId) return;

    const allSettings = allSharedSettings.filter((s) =>
      (s.integration_uuids || []).includes(integration.id),
    );
    if (allSettings.length === 0) return;

    async function checkLinkedValues() {
      const { data: allConfigured } =
        await ConfiguredIntegrationsApi.fetchConfiguredIntegrationsBySrmAccountId(
          srmAccountId,
        );
      if (!allConfigured) return;

      const currentId = integration.id;

      const buildAttributesToLoad = (setting, partner, partnerAttrs) =>
        (setting.mappings || []).reduce((acc, m) => {
          let sourceAttrName;
          let targetAttrName;
          if (
            m.to_integration_uuid === currentId &&
            m.from_integration_uuid === partner.uuid
          ) {
            sourceAttrName = m.from_attribute_name;
            targetAttrName = m.to_attribute_name;
          } else if (
            m.from_integration_uuid === currentId &&
            m.to_integration_uuid === partner.uuid
          ) {
            sourceAttrName = m.to_attribute_name;
            targetAttrName = m.from_attribute_name;
          }
          if (sourceAttrName && targetAttrName) {
            const { baseName: srcBase, subKey: srcKey } =
              parseAttributeKey(sourceAttrName);
            const partnerAttr = partnerAttrs.find((a) => a.name === srcBase);
            if (partnerAttr) {
              const resolvedValue =
                srcKey !== null && Array.isArray(partnerAttr.value)
                  ? getHeaderValue(partnerAttr.value, srcKey)
                  : partnerAttr.value;
              if (
                resolvedValue !== null &&
                resolvedValue !== undefined &&
                resolvedValue !== ''
              ) {
                // targetAttrName may be dot-notated (e.g. 'headers1.KarmakAccountNumber')
                acc.push({ name: targetAttrName, value: resolvedValue });
              }
            }
          }
          return acc;
        }, []);

      allSettings.some((setting) => {
        const partners = (setting.integrations || []).filter(
          (i) => i.uuid !== currentId,
        );
        return partners.some((partner) => {
          const partnerConfigured = allConfigured.find(
            (ci) => ci.attributes.integration_uuid === partner.uuid,
          );
          if (!partnerConfigured) return false;

          const partnerAttrs =
            partnerConfigured.attributes.location_settings_attributes || [];
          const hasValues = partnerAttrs.some(
            (a) => a.value !== null && a.value !== '' && a.value !== undefined,
          );
          if (!hasValues) return false;

          const attributesToLoad = buildAttributesToLoad(
            setting,
            partner,
            partnerAttrs,
          );
          if (attributesToLoad.length > 0) {
            setLinkedSourceData({
              settingName: setting.name,
              partnerName: partner.name,
              attributesToLoad,
            });
            return true;
          }
          return false;
        });
      });
    }

    checkLinkedValues();
  }, [integration?.id, location.attributes?.srm_account_id, allSharedSettings]);

  const { notify } = useNotifications();

  // Arriving from an add is the one lifecycle action with no notification of its
  // own — the toggle already raises one — so the cascade announces itself.
  const addAnnounced = useRef(false);

  // The peer links navigate to another location on this same route, carrying no
  // state, so the page is reused. The useState initialiser above only ever runs
  // once — without this the summary keeps describing the location left behind.
  useEffect(() => {
    setCascade(routerLocation.state?.sharedSettingSync || null);
  }, [identifier, routerLocation.state]);

  useEffect(() => {
    const sync = routerLocation.state?.sharedSettingSync;
    if (addAnnounced.current || sync?.action !== 'location_add') return;

    const results = sync.results || [];
    if (results.length === 0) return;

    addAnnounced.current = true;
    // Only a peer that was actually created and left off is waiting on data;
    // failed, excluded and not_present are different outcomes entirely.
    const created = results.filter((result) => result.status === 'created');
    const pending = created.filter((result) => !result.enabled).length;

    notify(
      { intent: 'success', title: t`Shared Setting sync`, onClose: noop },
      pending > 0
        ? t`Location added to ${created.length} linked integration(s): ${pending} still need data.`
        : t`Location added to ${created.length} linked integration(s).`,
    );
  }, [routerLocation.state, notify]);

  const successMessageBody = (data, e) =>
    `${integration?.attributes?.name} for ${data?.attributes?.name} (${data?.attributes?.srm_account_id}) was ${e}.`;

  const notificationsProps = (e) => {
    const str = e;

    return {
      intent: 'success',
      title: `${
        str.charAt(0).toUpperCase() + str.slice(1)
      } Integration Settings`,
      /* istanbul ignore next */
      onClose: noop,
    };
  };

  const handleToggle = (e) => {
    async function enableLocation() {
      const { data, meta } = await IntegrationsApi.toggleConfiguredIntegration(
        location.id,
        e === 'enabled',
      );

      /* istanbul ignore else */
      if (data) {
        setToggle(data.attributes?.enabled === true ? 'enabled' : 'disabled');
        setToggleBg(
          data.attributes?.enabled
            ? color.status.success.light
            : color.base.fullMoon,
        );
        // Enabling or disabling propagates across the Shared Setting, so report
        // what moved in the same summary the add uses, and re-read the peers so
        // their tags agree with it.
        setCascade(meta?.shared_setting_sync || null);
        setPeerStatesVersion((version) => version + 1);
        notify(notificationsProps(e), successMessageBody(data, e));
      }
    }

    enableLocation();
  };

  // Opting a location out of lifecycle sync keeps its attribute-value sync intact,
  // so one dealer can deviate without unlinking the whole Shared Setting.
  const handleSyncActionsToggle = (value) => {
    async function updateSyncActions() {
      const excluded = value === 'excluded';
      const { data } =
        await ConfiguredIntegrationsApi.updateConfiguredIntegration(
          location.id,
          { ignore_shared_setting_actions: excluded },
        );

      /* istanbul ignore else */
      if (data) {
        const nowExcluded = !!data.attributes?.ignore_shared_setting_actions;
        setSyncActions(nowExcluded ? 'excluded' : 'included');
        // The removal confirmation reads this off `location` to decide whether a
        // blast radius exists at all, so display state alone is not enough.
        setLocation((previous) => ({
          ...previous,
          attributes: {
            ...previous.attributes,
            ignore_shared_setting_actions: nowExcluded,
          },
        }));
        notify(
          {
            intent: 'success',
            title: t`Shared Settings sync`,
            onClose: noop,
          },
          excluded
            ? t`This location no longer follows enable, disable or remove from its group.`
            : t`This location follows enable, disable and remove from its group again.`,
        );
      }
    }

    updateSyncActions();
  };

  return locationNotFound ? (
    <Flex marginTop={2}>
      <NotFound
        title={t`Integration Location Not Found`}
        description={t`The Integration Location may have been removed or the URL is incorrect.`}
      />
    </Flex>
  ) : (
    <>
      <Flex paddingX={2} paddingY={0.5}>
        <Breadcrumbs current={current} path={path} />
      </Flex>

      <HeaderBlock flexDirection="column" justifyContent="space-between">
        <WrapperWithLoading loading={loading}>
          <Helmet title={location.attributes?.name} />
          <Flex alignItems="center" marginBottom={1}>
            <H1>
              {location.attributes?.name}
              (ID {location.attributes?.srm_account_id})
            </H1>
            {location.id && (
              <Flex marginLeft={1}>
                <CopyUrlButton
                  basePath="/location_settings"
                  id={location.id}
                  slug={location.attributes?.slug}
                />
              </Flex>
            )}
          </Flex>
          <span>
            <Trans>OEM</Trans> <b>{location.attributes?.oems}</b> •{' '}
            <Trans>Dealer Type</Trans>{' '}
            <b>{location.attributes?.account_type}</b> •{' '}
            <Trans>City, State </Trans>{' '}
            <b>
              {location.attributes?.city}, {location.attributes?.state}
            </b>
          </span>
        </WrapperWithLoading>
      </HeaderBlock>

      <HalfMoonBg>
        <Flex marginX={1}>
          <Grid.Container>
            <WrapperWithLoading loading={loading}>
              <Grid.Row>
                <Grid.Column span="3">
                  <EnableIntegration
                    style={{
                      backgroundColor: toColorString(toggleBg),
                    }}
                  >
                    <H3 marginBottom={1}>{integration.name}</H3>

                    <Toggle
                      name="toggle"
                      options={[
                        { id: 'disabled', text: 'Disabled' },
                        { id: 'enabled', text: 'Enabled' },
                      ]}
                      onChange={handleToggle}
                      selected={toggle}
                      disabled={toggleDisabled}
                    />
                  </EnableIntegration>
                  <SharedSettingsSection data-testid="shared-settings-section">
                    <H4>
                      <Trans>Shared Settings</Trans>
                    </H4>
                    {sidebarSharedSettings.length > 0 ? (
                      <>
                        {sidebarSharedSettings.map((setting) => {
                          const partnerIntegrations = (
                            setting.integrations || []
                          ).filter((i) => i.uuid !== integration.id);
                          return (
                            <SharedSettingGroup key={setting.id}>
                              <P size="small" shade={0} marginBottom={0}>
                                {setting.name}
                              </P>
                              {partnerIntegrations.map((partner) => {
                                const peer = peerStates?.[partner.uuid];
                                const state = peerStates
                                  ? locationStateTag(peer)
                                  : null;
                                // Only linkable where the location actually
                                // exists in the partner integration.
                                const peerPath = peer
                                  ? `/location_settings/${peer.slug || peer.id}`
                                  : null;
                                return (
                                  <PartnerRow key={partner.uuid}>
                                    <P size="small" shade={1} marginBottom={0}>
                                      {peerPath ? (
                                        <Link to={peerPath}>
                                          {partner.name}
                                        </Link>
                                      ) : (
                                        partner.name
                                      )}
                                    </P>
                                    {state && (
                                      <Tag
                                        text={state.statusText}
                                        color={state.statusColor}
                                      />
                                    )}
                                  </PartnerRow>
                                );
                              })}
                            </SharedSettingGroup>
                          );
                        })}
                        <SyncOptOut>
                          <FeatureInfoDrawer
                            heading={<Trans>Shared Settings sync</Trans>}
                            ariaLabel={t`More information about Shared Settings sync`}
                          >
                            <SyncOptOutInfoContent />
                          </FeatureInfoDrawer>
                          <Toggle
                            name="sync-actions-toggle"
                            options={[
                              { id: 'excluded', text: t`Excluded` },
                              { id: 'included', text: t`Included` },
                            ]}
                            onChange={handleSyncActionsToggle}
                            selected={syncActions}
                          />
                        </SyncOptOut>
                      </>
                    ) : (
                      <P size="small" shade={1}>
                        <Trans>No shared settings configured.</Trans>
                      </P>
                    )}
                  </SharedSettingsSection>
                  <RemoveIntegration>
                    <H4>
                      <Trans>Remove Integration?</Trans>
                    </H4>

                    <P shade={1} size="small">
                      <Trans>You can always add it back.</Trans>
                    </P>

                    <Flex marginTop={1}>
                      <Button
                        text="Remove Integration"
                        icon={iconix.MinusCircle}
                        variant="ghost"
                        iconPosition="left"
                        onClick={handleModal}
                      />
                    </Flex>
                  </RemoveIntegration>
                </Grid.Column>
                <Grid.Column span="9">
                  {cascade?.results?.length > 0 && (
                    <CascadeSummary
                      action={cascade.action}
                      results={cascade.results}
                      onDismiss={() => setCascade(null)}
                    />
                  )}
                  {location.id && (
                    <Form
                      key={formKey}
                      locationSettingsSchema={locationSettingsSchema}
                      configuredIntegration={location}
                      locationSettingsAttributes={
                        location.attributes?.location_settings_attributes
                      }
                      setLocation={setLocation}
                      setToggleDisabled={setToggleDisabled}
                      integration={integration}
                      setFormKey={setFormKey}
                      allSharedSettings={allSharedSettings}
                      linkedSourceData={linkedSourceData}
                      setLinkedSourceData={setLinkedSourceData}
                    />
                  )}
                  <Grid.Container>
                    <LocationContainer>
                      <LocationExecutionLog
                        setRefresh={setRefresh}
                        location={location}
                      />
                    </LocationContainer>
                  </Grid.Container>
                  {integration.id && location.id && (
                    <Grid.Container>
                      <ChangeHistoryContainer
                        integration={integration}
                        location={location}
                        title={t`Change History`}
                      />
                    </Grid.Container>
                  )}
                </Grid.Column>
              </Grid.Row>
            </WrapperWithLoading>
          </Grid.Container>
        </Flex>
      </HalfMoonBg>

      <RemoveLocationModal
        visible={visible}
        onClose={handleModal}
        integration={integration}
        location={location}
        sharedSettings={sidebarSharedSettings}
        sharedSettingsLoading={sharedSettingsLoading}
      />
    </>
  );
}
